import {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode
} from "react";
import CryptoJS from "crypto-js";

interface AnalyticsUserData {
  [key: string]: unknown;
}

interface AnalyticsUserDataContextType {
  analyticsUserData: AnalyticsUserData;
  updUserData: (key: string, value: unknown) => void;
}

const AnalyticsUserDataContext = createContext<AnalyticsUserDataContextType | undefined>(
  undefined
);

export const useAnalyticsUserData = (): AnalyticsUserDataContextType => {
  const context = useContext(AnalyticsUserDataContext);
  if (!context) {
    throw new Error(
      "useAnalyticsUserData must be used within AnalyticsUserDataProvider"
    );
  }
  return context;
};

interface AnalyticsUserDataProviderProps {
  children: ReactNode;
}

export const AnalyticsUserDataProvider = ({
  children,
}: AnalyticsUserDataProviderProps) => {
  const SECRET_KEY = process.env.REACT_APP_SECRET_KEY;

  const [analyticsUserData, setAnalyticsUserData] = useState<AnalyticsUserData>({});

  const encryptData = (data: AnalyticsUserData): string => {
    const jsonString = JSON.stringify(data);
    const encrypted = CryptoJS.AES.encrypt(jsonString, SECRET_KEY!).toString();
    return encrypted;
  };

  const decryptData = (encryptedData: string): AnalyticsUserData | null => {
    try {
      const decrypted = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY!);
      const jsonString = decrypted.toString(CryptoJS.enc.Utf8);
      return JSON.parse(jsonString) as AnalyticsUserData;
    } catch (error) {
      console.error("Failed to decrypt data:", error);
      return null;
    }
  };

  const updUserData = (key: string, value: unknown): void => {
    setAnalyticsUserData((prevData) => {
      const newData: AnalyticsUserData = {
        ...prevData,
        [key]: value,
      };

      const encryptedData = encryptData(newData);
      localStorage.setItem("memoryOS_AnalyticsUserData", encryptedData);

      return newData;
    });
  };

  const initializeUserData = (): void => {
    const encryptedData = localStorage.getItem("memoryOS_AnalyticsUserData");
    if (encryptedData) {
      const decryptedData = decryptData(encryptedData);
      if (decryptedData) {
        setAnalyticsUserData(decryptedData);
      }
    }
  };

  useEffect(() => {
    initializeUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue: AnalyticsUserDataContextType = {
    analyticsUserData,
    updUserData,
  };

  return (
    <AnalyticsUserDataContext.Provider value={contextValue}>
      {children}
    </AnalyticsUserDataContext.Provider>
  );
};